"use client";

import Image from "next/image";
import Link from "next/link";
import { FiArrowUpRight, FiArrowDownRight, FiEye, FiX } from "react-icons/fi";
import { marketPageUrl } from "@/lib/source-url";
import { trackRecommendation, setNavigationSource } from "@/lib/recommendation-tracker";
import { formatCents } from '@/lib/format-cents';

export interface TradeToastData {
  side: "BUY" | "SELL";
  outcome: string;
  shares: number;
  price: number;
  notionalValue: number;
  marketQuestion: string;
  marketSlug: string;
  eventSlug?: string | null;
  isMultiOutcome?: boolean;
  marketImage: string | null;
  profitLoss?: number;
  profitLossPercent?: number;
  source?: "polymarket" | "kalshi" | "predict" | "hyperliquid";
  isLimit?: boolean; // Limit order (pending, not yet filled)
}

interface TradeToastProps {
  data: TradeToastData;
  type?: "manual" | "redemption";
  /** Optional dismiss callback — when set the toast renders an X in the
   *  top-right so the user can close it before the auto-dismiss timer
   *  fires. Matches the CopyTradeToast / PositionResolvedToast family. */
  onClose?: () => void;
}

export default function TradeToast({ data, type = "manual", onClose }: TradeToastProps) {
  const isBuy = data.side === "BUY";
  const isRedemption = type === "redemption";
  const isLimit = data.isLimit;

  const label = isRedemption ? "Redeemed" : isLimit ? "Limit Placed" : isBuy ? "Bought" : "Sold";
  const accentColor = isRedemption
    ? "text-amber-400"
    : isLimit ? "text-amber-400"
    : isBuy ? "text-emerald-500" : "text-red-400";
  const accentBg = isRedemption
    ? "bg-amber-400/10"
    : isLimit ? "bg-amber-400/10"
    : isBuy ? "bg-emerald-500/10" : "bg-red-400/10";
  // Pill label inside the hero block. Manual buys/sells get the side;
  // limit orders get "LIMIT" so the chip says what just happened ("the
  // order is on the book") rather than what side it was on — that's
  // already in the outcome row above. Redemptions get "CLAIM".
  const pillLabel = isRedemption
    ? "CLAIM"
    : isLimit
      ? "LIMIT"
      : isBuy ? "BUY" : "SELL";

  const marketHref = data.source === 'kalshi'
    ? `/kalshi/event/${data.marketSlug}`
    : data.source === 'hyperliquid'
      ? `/hyperliquid/event/${encodeURIComponent(data.marketSlug)}`
      : marketPageUrl({ marketSlug: data.marketSlug, eventSlug: data.eventSlug, outcome: data.outcome, source: 'polymarket', isMultiOutcome: data.isMultiOutcome });

  return (
    <div className="relative bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#25252B] rounded-[5px] p-4 min-w-[320px] max-w-[380px] shadow-xl shadow-black/10 dark:shadow-black/40">
      {/* Dismiss button — mirrors CopyTradeToast / PositionResolvedToast
          so the user can close the popup once they've read it instead
          of waiting for the 8s auto-dismiss. Only rendered when
          `onClose` is passed (kept optional so legacy call sites that
          don't wire toast.dismiss don't ship an inert X). */}
      {onClose && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label="Cerrar"
          className="absolute top-2 right-2 p-1 rounded-[4px] text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors z-10"
        >
          <FiX className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Top row: action chip + outcome label */}
      <div className={`flex items-center justify-between mb-3 gap-2 ${onClose ? "pr-6" : ""}`}>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-[5px] ${accentBg}`}>
          {isBuy ? (
            <FiArrowUpRight className={`w-3.5 h-3.5 ${accentColor}`} />
          ) : (
            <FiArrowDownRight className={`w-3.5 h-3.5 ${accentColor}`} />
          )}
          <span className={`text-[11px] font-semibold ${accentColor}`}>
            {label}
          </span>
        </div>
        {data.outcome ? (
          <span className={`text-[11px] font-medium ${
            (data.outcome || "").toUpperCase() === "YES" ? "text-emerald-500" : "text-red-400"
          }`}>
            {data.outcome}
          </span>
        ) : null}
      </div>

      {/* Market info */}
      <div className="flex items-start gap-3 mb-3">
        {data.marketImage ? (
          <div className="relative w-10 h-10 rounded-[5px] overflow-hidden flex-shrink-0">
            <Image src={data.marketImage} alt="" fill className="object-cover" />
          </div>
        ) : (
          <div className={`w-10 h-10 rounded-[5px] flex items-center justify-center flex-shrink-0 ${accentBg}`}>
            {isBuy ? (
              <FiArrowUpRight className={`w-5 h-5 ${accentColor}`} />
            ) : (
              <FiArrowDownRight className={`w-5 h-5 ${accentColor}`} />
            )}
          </div>
        )}
        <p className="text-sm text-gray-900 dark:text-white font-medium line-clamp-2 leading-snug flex-1">
          {data.marketQuestion}
        </p>
      </div>

      {/* Hero block — centered notional + side pill + shares@price subtitle.
          Mirrors CopyTradeToast / AutoResumeToast / PositionResolvedToast so
          a vertical stack of toasts reads as one design family. */}
      <div className={`py-3 px-3 rounded-[5px] mb-3 ${accentBg} text-center`}>
        <div className={`text-2xl font-bold leading-none ${accentColor}`}>
          ${(data.notionalValue ?? 0).toFixed(2)}
        </div>
        <div className="flex items-center justify-center gap-1.5 mt-2 text-[11px]">
          <span className={`px-1.5 py-0.5 rounded-[3px] font-semibold ${accentBg} ${accentColor}`}>
            {pillLabel}
          </span>
          <span className="text-gray-500 dark:text-gray-400">
            {(data.shares ?? 0).toFixed(1)}
            {!isRedemption ? ` @ ${formatCents((data.price ?? 0), 2)}` : " shares"}
          </span>
        </div>
      </div>

      {/* P&L for sells/redemptions */}
      {!isBuy && data.profitLoss != null && (
        <div className={`flex items-center justify-center gap-2 py-2 px-3 rounded-[5px] mb-3 ${
          data.profitLoss >= 0 ? "bg-emerald-500/10" : "bg-red-400/10"
        }`}>
          <span className={`text-sm font-semibold ${data.profitLoss >= 0 ? "text-emerald-500" : "text-red-400"}`}>
            {data.profitLoss >= 0 ? "+" : ""}${(data.profitLoss ?? 0).toFixed(2)}
          </span>
          {data.profitLossPercent != null && (
            <span className={`text-xs ${data.profitLoss >= 0 ? "text-emerald-500/70" : "text-red-400/70"}`}>
              ({data.profitLossPercent >= 0 ? "+" : ""}{(data.profitLossPercent ?? 0).toFixed(1)}%)
            </span>
          )}
        </div>
      )}

      {/* View market */}
      {data.marketSlug && (
        <Link
          href={marketHref}
          className="flex items-center justify-center gap-1.5 w-full py-2 rounded-[5px] text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          onClick={(e) => { e.stopPropagation(); trackRecommendation(data.marketSlug!, 'view_detail', 'notification'); setNavigationSource('notification'); }}
        >
          <FiEye className="w-3.5 h-3.5" />
          View Market
        </Link>
      )}
    </div>
  );
}
